import { useState } from 'react';
import { Search, Download } from 'lucide-react';
import { getDisplayStatus, exportCSV } from '../../utils/reportHelpers';

const fmt = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const STATUS_LABEL = {
  approved:         'Approved',
  booked:           'Booked',
  travelling:       'Travelling',
  completed:        'Completed',
  pending_approval: 'Pending Approval',
  draft:            'Draft',
  declined:         'Declined',
  cancelled:        'Cancelled',
};

const STATUS_COLOR = {
  approved:         { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  booked:           { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  travelling:       { bg: '#f0fdfa', color: '#0d9488', border: '#99f6e4' },
  completed:        { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  pending_approval: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  draft:            { bg: '#fafafa', color: '#94a3b8', border: '#e2e8f0' },
  declined:         { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  cancelled:        { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
};

const SECTOR_LABEL = {
  flight:        'Flight',
  accommodation: 'Accommodation',
  'car-hire':    'Car Hire',
  parking:       'Parking',
  transfers:     'Transfer',
  meals:         'Meals',
  other:         'Other',
};

function matchesDate(sector, date) {
  switch (sector.type) {
    case 'accommodation': {
      if (!sector.checkIn) return false;
      const co = sector.checkOut || sector.checkIn;
      // Stay covers the date (inclusive of check-in, inclusive of check-out night)
      return sector.checkIn <= date && co >= date;
    }
    case 'car-hire': {
      if (!sector.pickupDate) return false;
      const drop = sector.dropOffDate || sector.pickupDate;
      return sector.pickupDate <= date && drop >= date;
    }
    case 'parking': {
      if (!sector.entryDate) return false;
      const exit = sector.exitDate || sector.entryDate;
      return sector.entryDate <= date && exit >= date;
    }
    default:
      return (sector.date || '') === date;
  }
}

function sectorDetails(sector, trip) {
  switch (sector.type) {
    case 'flight':        return [sector.from, sector.to].filter(Boolean).join(' → ') || '—';
    case 'accommodation': return sector.propertyName || trip.destinationCity || '—';
    case 'car-hire':      return [sector.pickupLocation, sector.dropOffLocation].filter(Boolean).join(' → ') || sector.company || '—';
    case 'parking':       return sector.facility || '—';
    case 'transfers':     return [sector.from, sector.to].filter(Boolean).join(' → ') || '—';
    case 'meals':         return sector.venue || '—';
    default:              return sector.notes ? sector.notes.slice(0, 60) : '—';
  }
}

function sectorDateRange(sector) {
  switch (sector.type) {
    case 'accommodation': return { from: sector.checkIn, to: sector.checkOut };
    case 'car-hire':      return { from: sector.pickupDate, to: sector.dropOffDate };
    case 'parking':       return { from: sector.entryDate, to: sector.exitDate };
    default:              return { from: sector.date, to: null };
  }
}

function StatusBadge({ status }) {
  const sc = STATUS_COLOR[status] || STATUS_COLOR.draft;
  return (
    <span
      className="inline-block text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function TravelDayLookupReport({ trips }) {
  const [mode, setMode] = useState('date');
  const [dateInput, setDateInput] = useState(new Date().toISOString().slice(0, 10));
  const [flightInput, setFlightInput] = useState('');
  const [flightDateInput, setFlightDateInput] = useState('');
  const [depCity, setDepCity] = useState('');
  const [arrCity, setArrCity] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLabel, setSearchLabel] = useState('');

  const canSearch = mode === 'date' ? !!dateInput : !!flightInput.trim();

  const handleSearch = () => {
    const rows = [];

    if (mode === 'date') {
      trips.forEach(trip => {
        (trip.sectors || []).forEach(sector => {
          if (matchesDate(sector, dateInput)) rows.push({ trip, sector });
        });
      });
      rows.sort((a, b) => (a.trip.travellerName || '').localeCompare(b.trip.travellerName || ''));
      setSearchLabel(fmt(dateInput));
    } else {
      const target = flightInput.trim().toUpperCase().replace(/\s+/g, '');
      if (!target) return;
      trips.forEach(trip => {
        (trip.sectors || []).forEach(sector => {
          if (
            sector.type === 'flight' &&
            sector.flightNumber &&
            sector.flightNumber.toUpperCase().replace(/\s+/g, '') === target &&
            (!flightDateInput || sector.date === flightDateInput)
          ) {
            rows.push({ trip, sector });
          }
        });
      });
      rows.sort((a, b) => {
        const dc = (a.sector.date || '').localeCompare(b.sector.date || '');
        return dc !== 0 ? dc : (a.trip.travellerName || '').localeCompare(b.trip.travellerName || '');
      });
      setSearchLabel(`flight ${flightInput.trim().toUpperCase()}${flightDateInput ? ` on ${fmt(flightDateInput)}` : ''}`);
    }

    const dep = depCity.trim().toLowerCase();
    const arr = arrCity.trim().toLowerCase();
    const filtered = rows.filter(({ sector }) => {
      if (dep && !(sector.from || '').toLowerCase().includes(dep)) return false;
      if (arr && !(sector.to || '').toLowerCase().includes(arr)) return false;
      return true;
    });

    setResults(filtered);
    setHasSearched(true);
  };

  const handleKey = (e) => { if (e.key === 'Enter' && canSearch) handleSearch(); };

  const handleExportCSV = () => {
    if (mode === 'date') {
      const headers = ['Traveller', 'Status', 'Sector Type', 'Details / Route', 'Flight #', 'Dep Time', 'Arr Time', 'Date(s)', 'Cost Centre'];
      const rows = results.map(({ trip, sector }) => {
        const { from: df, to: dt } = sectorDateRange(sector);
        const dates = dt && dt !== df ? `${fmt(df)} – ${fmt(dt)}` : fmt(df);
        return [
          trip.travellerName || '',
          STATUS_LABEL[getDisplayStatus(trip)] || trip.status || '',
          SECTOR_LABEL[sector.type] || sector.type || '',
          sectorDetails(sector, trip),
          sector.flightNumber || '',
          sector.departureTime || '',
          sector.arrivalTime || '',
          dates,
          trip.costCentre || '',
        ];
      });
      exportCSV([headers, ...rows], `travel_lookup_${dateInput}.csv`);
    } else {
      const headers = ['Traveller', 'Status', 'Flight Date', 'From', 'To', 'Flight #', 'Dep Time', 'Arr Time', 'Destination', 'Cost Centre'];
      const rows = results.map(({ trip, sector }) => [
        trip.travellerName || '',
        STATUS_LABEL[getDisplayStatus(trip)] || trip.status || '',
        fmt(sector.date),
        sector.from || '',
        sector.to || '',
        sector.flightNumber || '',
        sector.departureTime || '',
        sector.arrivalTime || '',
        trip.destinationCity || '',
        trip.costCentre || '',
      ]);
      exportCSV([headers, ...rows], `flight_lookup_${flightInput.trim().toUpperCase()}.csv`);
    }
  };

  const switchMode = (m) => { setMode(m); setHasSearched(false); setResults([]); };

  return (
    <div className="space-y-5">

      {/* Mode toggle */}
      <div className="flex bg-white border border-gray-200 rounded-lg p-1 w-fit shadow-sm">
        {[['date', 'By Date'], ['flight', 'By Flight Number']].map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              mode === m ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          {mode === 'date' ? (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Travel Date</label>
              <input
                type="date"
                value={dateInput}
                onChange={e => setDateInput(e.target.value)}
                onKeyDown={handleKey}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Flight Number</label>
                <input
                  type="text"
                  value={flightInput}
                  onChange={e => setFlightInput(e.target.value.toUpperCase())}
                  onKeyDown={handleKey}
                  placeholder="e.g. QF123"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 w-36 uppercase focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Date <span className="normal-case font-normal tracking-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={flightDateInput}
                  onChange={e => setFlightDateInput(e.target.value)}
                  onKeyDown={handleKey}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </>
          )}
          <button
            onClick={handleSearch}
            disabled={!canSearch}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors ${
              canSearch ? 'bg-gray-900 hover:bg-gray-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            <Search size={14} aria-hidden="true" />
            Search
          </button>
        </div>

        {/* Optional city filters */}
        <div className="flex flex-wrap items-end gap-3 pt-4 border-t border-gray-100">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              From <span className="normal-case font-normal tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={depCity}
              onChange={e => setDepCity(e.target.value)}
              onKeyDown={handleKey}
              placeholder="e.g. SYD"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 w-36 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              To <span className="normal-case font-normal tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={arrCity}
              onChange={e => setArrCity(e.target.value)}
              onKeyDown={handleKey}
              placeholder="e.g. MEL"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 w-36 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {(depCity || arrCity) && (
            <button
              type="button"
              onClick={() => { setDepCity(''); setArrCity(''); }}
              className="px-3 py-1.5 bg-gray-100 text-gray-600 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200"
            >
              Clear
            </button>
          )}
        </div>

        {mode === 'flight' && (
          <p className="text-xs text-gray-400">
            Returns all trips that include this flight number. Optionally filter by date, departure, or arrival.
          </p>
        )}
      </div>

      {/* Results */}
      {hasSearched && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-600">
              {results.length === 0
                ? `No results for ${searchLabel}`
                : `${results.length} result${results.length !== 1 ? 's' : ''} for ${searchLabel}`}
              {depCity && ` · from ${depCity}`}
              {arrCity && ` · to ${arrCity}`}
            </p>
            {results.length > 0 && (
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-semibold hover:bg-teal-700"
              >
                <Download size={13} aria-hidden="true" />
                Export CSV
              </button>
            )}
          </div>

          {results.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
              <Search size={32} className="mx-auto text-gray-300 mb-3" aria-hidden="true" />
              <p className="text-sm font-semibold text-gray-500">No trips found</p>
              <p className="text-xs text-gray-400 mt-1">
                {mode === 'date'
                  ? 'No sectors are scheduled for this date'
                  : 'No trips contain this flight number'}
              </p>
            </div>
          ) : mode === 'date' ? (
            <DateResultsTable results={results} />
          ) : (
            <FlightResultsTable results={results} />
          )}
        </div>
      )}
    </div>
  );
}

function DateResultsTable({ results }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label="Travel day lookup results">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {['Traveller', 'Status', 'Type', 'Details / Route', 'Flight #', 'Dep', 'Arr', 'Date(s)', 'Cost Centre'].map(h => (
                <th key={h} scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map(({ trip, sector }, idx) => {
              const status = getDisplayStatus(trip);
              const { from: df, to: dt } = sectorDateRange(sector);
              const dateRange = dt && dt !== df ? `${fmt(df)} – ${fmt(dt)}` : (df ? fmt(df) : '—');
              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                    {trip.travellerName || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={status} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {SECTOR_LABEL[sector.type] || sector.type || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 font-medium">
                    {sectorDetails(sector, trip)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">
                    {sector.flightNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {sector.departureTime || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {sector.arrivalTime || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {dateRange}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {trip.costCentre || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlightResultsTable({ results }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label="Flight lookup results">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {['Traveller', 'Status', 'Flight Date', 'Route', 'Flight #', 'Dep', 'Arr', 'Destination', 'Cost Centre'].map(h => (
                <th key={h} scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map(({ trip, sector }, idx) => {
              const status = getDisplayStatus(trip);
              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                    {trip.travellerName || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={status} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {fmt(sector.date)}
                  </td>
                  <td className="px-4 py-3 text-gray-800 font-medium whitespace-nowrap">
                    {[sector.from, sector.to].filter(Boolean).join(' → ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">
                    {sector.flightNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {sector.departureTime || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {sector.arrivalTime || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {trip.destinationCity || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {trip.costCentre || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
